import { useEffect, useState, useCallback } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { queryClient } from '@/lib/queryClient';
import { router } from '@/router';
import { ErrorBoundary, ToastContainer, PageLoader } from '@/components/shared';
import { useAuthStore } from '@/stores/auth.store';
import { initializeAuth } from '@/lib/auth/sso';

function App() {
  const [isInitializing, setIsInitializing] = useState(true);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setLoading = useAuthStore((state) => state.setLoading);
  const refreshFromCookies = useAuthStore((state) => state.refreshFromCookies);

  const initAuth = useCallback(async () => {
    setLoading(true);
    
    try {
      const result = await initializeAuth();
      
      if (result.success && result.token && result.user) {
        setAuthenticated(
          result.token,
          result.refreshToken || '',
          result.expiresAt || Date.now() + 24 * 60 * 60 * 1000,
          result.user
        );
      } else {
        // Not authenticated - that's okay, user will see landing page
        setLoading(false);
      }
    } catch (error) {
      console.error('Auth initialization failed:', error);
      setLoading(false);
    } finally {
      setIsInitializing(false);
    }
  }, [setAuthenticated, setLoading]);

  useEffect(() => {
    // Initialize authentication on app startup
    initAuth();

    // Set up interval to periodically check cookie changes
    // (in case user logs in/out in another tab)
    const intervalId = setInterval(() => {
      refreshFromCookies();
    }, 5000); // Check every 5 seconds

    return () => clearInterval(intervalId);
  }, [initAuth, refreshFromCookies]);

  // Show loader while checking authentication
  if (isInitializing) {
    return <PageLoader />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ToastContainer />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
